import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import {
  FiSmartphone,
  FiTruck,
  FiPieChart,
  FiCreditCard,
  FiLayers,
  FiUsers,
  FiDatabase,
  FiGlobe,
  FiBell,
  FiMoon,
  FiTag,
  FiMessageSquare,
} from "react-icons/fi";

const Features = () => {
  const { t, language } = useLanguage();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="container-custom text-center max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {t("featuresHeroTitle")}
          </h1>
          <p className="text-xl text-gray-700 mb-10">
            {t("featuresHeroDescription")}
          </p>
          <div className="flex justify-center">
            <a
              href="https://foodapp.solylock.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              {t("tryDemo")}
            </a>
          </div>
        </div>
      </section>

      {/* Customer App Features */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              {t("customerMobileApp")}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t("customerAppDescription")}
            </p>
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            <motion.div variants={itemVariants} className="feature-card">
              <div className="mb-4 bg-orange-100 inline-block p-3 rounded-lg">
                <FiSmartphone className="h-6 w-6 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">
                {t("intuitiveMenuBrowsing")}
              </h3>
              <p className="text-gray-600">{t("intuitiveMenuBrowsingDesc")}</p>
            </motion.div>

            <motion.div variants={itemVariants} className="feature-card">
              <div className="mb-4 bg-green-100 inline-block p-3 rounded-lg">
                <FiCreditCard className="h-6 w-6 text-green-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">
                {t("securePaymentOptions")}
              </h3>
              <p className="text-gray-600">{t("securePaymentOptionsDesc")}</p>
            </motion.div>

            <motion.div variants={itemVariants} className="feature-card">
              <div className="mb-4 bg-blue-100 inline-block p-3 rounded-lg">
                <FiTruck className="h-6 w-6 text-blue-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">
                {t("realTimeOrderTracking")}
              </h3>
              <p className="text-gray-600">{t("realTimeOrderTrackingDesc")}</p>
            </motion.div>

            <motion.div variants={itemVariants} className="feature-card">
              <div className="mb-4 bg-purple-100 inline-block p-3 rounded-lg">
                <FiTag className="h-6 w-6 text-purple-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Special Discounts</h3>
              <p className="text-gray-600">
                Apply discount codes and view special offers on categories or
                individual products.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="feature-card">
              <div className="mb-4 bg-yellow-100 inline-block p-3 rounded-lg">
                <FiBell className="h-6 w-6 text-yellow-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Push Notifications</h3>
              <p className="text-gray-600">
                Receive real-time updates about your order status, special
                promotions, and more.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="feature-card">
              <div className="mb-4 bg-red-100 inline-block p-3 rounded-lg">
                <FiMoon className="h-6 w-6 text-red-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Light & Dark Themes</h3>
              <p className="text-gray-600">
                Choose between light and dark themes for comfortable browsing
                during day or night.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="feature-card">
              <div className="mb-4 bg-indigo-100 inline-block p-3 rounded-lg">
                <FiGlobe className="h-6 w-6 text-indigo-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Multi-language Support</h3>
              <p className="text-gray-600">
                Use the app in your preferred language with support for Turkish,
                Finnish, and English.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="feature-card">
              <div className="mb-4 bg-teal-100 inline-block p-3 rounded-lg">
                <FiMessageSquare className="h-6 w-6 text-teal-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">In-app Messaging</h3>
              <p className="text-gray-600">
                Contact restaurant staff directly through the app for special
                requests or questions.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Restaurant Admin Features */}
      <section className="section bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">
              {t("restaurantAdminDashboard")}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t("restaurantAdminDashboardDesc")}
            </p>
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            <motion.div variants={itemVariants} className="feature-card">
              <div className="mb-4 bg-indigo-100 inline-block p-3 rounded-lg">
                <FiLayers className="h-6 w-6 text-indigo-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Menu Management</h3>
              <p className="text-gray-600">
                Create and update menus, adjust pricing, and control item
                availability in real-time, all from your mobile device.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="feature-card">
              <div className="mb-4 bg-teal-100 inline-block p-3 rounded-lg">
                <FiPieChart className="h-6 w-6 text-teal-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Analytics & Reporting</h3>
              <p className="text-gray-600">
                Detailed insights into sales, popular items, peak hours, and
                customer preferences right on your mobile device.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="feature-card">
              <div className="mb-4 bg-pink-100 inline-block p-3 rounded-lg">
                <FiUsers className="h-6 w-6 text-pink-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Order Management</h3>
              <p className="text-gray-600">
                Accept, prepare, and manage orders in real-time with instant
                notifications when new orders arrive.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="feature-card">
              <div className="mb-4 bg-amber-100 inline-block p-3 rounded-lg">
                <FiDatabase className="h-6 w-6 text-amber-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Product Management</h3>
              <p className="text-gray-600">
                Easily manage your inventory, add products, update prices, and
                organize categories from anywhere.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="feature-card">
              <div className="mb-4 bg-cyan-100 inline-block p-3 rounded-lg">
                <FiTag className="h-6 w-6 text-cyan-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Discount Management</h3>
              <p className="text-gray-600">
                Create and manage special offers, discount codes, and
                promotional pricing for categories or individual products.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="feature-card">
              <div className="mb-4 bg-emerald-100 inline-block p-3 rounded-lg">
                <FiBell className="h-6 w-6 text-emerald-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">
                Real-time Notifications
              </h3>
              <p className="text-gray-600">
                Get instant alerts for new orders, order status changes, and
                important business updates.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="feature-card">
              <div className="mb-4 bg-blue-100 inline-block p-3 rounded-lg">
                <FiMoon className="h-6 w-6 text-blue-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Dark Mode Support</h3>
              <p className="text-gray-600">
                Enjoy eye-friendly dark mode when working late hours or in
                low-light environments.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="feature-card">
              <div className="mb-4 bg-orange-100 inline-block p-3 rounded-lg">
                <FiGlobe className="h-6 w-6 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Multilingual Interface</h3>
              <p className="text-gray-600">
                Admin interface available in multiple languages: Turkish,
                Finnish, and English.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="feature-card">
              <div className="mb-4 bg-purple-100 inline-block p-3 rounded-lg">
                <FiSmartphone className="h-6 w-6 text-purple-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Mobile Optimized</h3>
              <p className="text-gray-600">
                Fully responsive admin interface designed specifically for
                smartphones and tablets.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-6">
            {t("readyToSeeByteBelly")}
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            {t("checkoutLiveDemo")}
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <a
              href="https://foodapp.solylock.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              {t("tryDemo")}
            </a>
            <Link to="/pricing" className="btn btn-secondary">
              {t("viewPricing")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Features;
